import { Link } from "react-router";
import { useNavigate } from "react-router";
import { useTranslations } from "use-intl";
import { useState, useTransition } from "react";
import { ArrowDown, ArrowUp, ChevronRight, Dumbbell, Footprints, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { addWorkout, moveWorkout, updatePlan } from "@/lib/actions";
import { DayPicker } from "@/components/app/day-picker";
import { EditLocaleToggle, useEditLocale } from "@/components/app/edit-locale";
import { Section } from "@/components/app/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { LocalizedList, LocalizedText, Phase } from "@liftquest/shared/types";
import type { Locale } from "@liftquest/shared/localized";

type PlanData = {
  id: string;
  name: LocalizedText;
  description: LocalizedText;
  startDate: string;
  trainingDays: number[];
  cardioDays: number[];
  warmup: LocalizedList;
  tips: LocalizedList;
  phases: Phase[];
};

type WorkoutSummary = {
  id: string;
  name: string;
  focus: string;
  kind: "strength" | "cardio";
  exercises: string[];
  /** Estimated duration in the current plan week (0 = no exercises). */
  minutes: number;
};

function useSaver() {
  const t = useTranslations("common");
  const [pending, start] = useTransition();
  const save = (fn: () => Promise<unknown>) =>
    start(async () => {
      try {
        await fn();
        toast.success(t("saved"));
      } catch {
        toast.error(t("error"));
      }
    });
  return [pending, save] as const;
}

export function PlanEditor({
  plan,
  currentWeek,
  workouts,
}: {
  plan: PlanData;
  currentWeek: number;
  workouts: WorkoutSummary[];
}) {
  const [editLocale, setEditLocale] = useEditLocale();
  return (
    <div className="mt-4 space-y-2">
      <EditLocaleToggle value={editLocale} onChange={setEditLocale} />
      <GeneralSection key={`g-${editLocale}`} plan={plan} locale={editLocale} currentWeek={currentWeek} />
      <WorkoutsSection planId={plan.id} workouts={workouts} />
      <PhasesSection key={`p-${editLocale}`} plan={plan} locale={editLocale} />
      <ListSection key={`w-${editLocale}`} plan={plan} locale={editLocale} field="warmup" />
      <ListSection key={`t-${editLocale}`} plan={plan} locale={editLocale} field="tips" />
    </div>
  );
}

function GeneralSection({ plan, locale, currentWeek }: { plan: PlanData; locale: Locale; currentWeek: number }) {
  const t = useTranslations("planEditor");
  const tc = useTranslations("common");
  const [name, setName] = useState(plan.name[locale] ?? "");
  const [description, setDescription] = useState(plan.description[locale] ?? "");
  const [startDate, setStartDate] = useState(plan.startDate);
  const [trainingDays, setTrainingDays] = useState(plan.trainingDays);
  const [cardioDays, setCardioDays] = useState(plan.cardioDays);
  const [pending, save] = useSaver();

  return (
    <Section title={t("general")}>
      <Card className="py-4">
        <CardContent className="space-y-4 px-4">
          <div className="grid gap-1.5">
            <Label htmlFor="plan-name">{t("name")}</Label>
            <Input id="plan-name" value={name} onChange={(e) => setName(e.target.value)} className="h-11 text-base" />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="plan-desc">{t("description")}</Label>
            <Textarea
              id="plan-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-24 text-base"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="plan-start">{t("startDate")}</Label>
            <Input
              id="plan-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-11 text-base"
            />
            <p className="text-xs text-muted-foreground">
              {t("startDateHelp")} {t("currentWeek", { week: currentWeek })}
            </p>
          </div>
          <div className="grid gap-1.5">
            <Label>{t("trainingDays")}</Label>
            <DayPicker value={trainingDays} onChange={setTrainingDays} disabledDays={cardioDays} />
            <p className="text-xs text-muted-foreground">{t("daysHelp")}</p>
          </div>
          <div className="grid gap-1.5">
            <Label>{t("cardioDays")}</Label>
            <DayPicker value={cardioDays} onChange={setCardioDays} disabledDays={trainingDays} />
          </div>
          <Button
            className="w-full"
            size="lg"
            disabled={pending}
            onClick={() =>
              save(() =>
                updatePlan(plan.id, {
                  name: { ...plan.name, [locale]: name },
                  description: { ...plan.description, [locale]: description },
                  startDate,
                  trainingDays,
                  cardioDays,
                }),
              )
            }
          >
            {pending && <Loader2 className="animate-spin" />} {tc("save")}
          </Button>
        </CardContent>
      </Card>
    </Section>
  );
}

function WorkoutsSection({ planId, workouts }: { planId: string; workouts: WorkoutSummary[] }) {
  const t = useTranslations("planEditor");
  const tc = useTranslations("common");
  const td = useTranslations("dashboard");
  const navigate = useNavigate();
  const [pending, start] = useTransition();
  const letter = String.fromCharCode(65 + workouts.filter((w) => w.kind === "strength").length);

  return (
    <Section title={t("workouts")}>
      <p className="-mt-1 mb-2 text-xs text-muted-foreground">{t("workoutsHelp")}</p>
      <ul className="space-y-2">
        {workouts.map((w, i) => (
          <li key={w.id}>
            <Card className="gap-0 py-0">
              <div className="flex items-center">
                <Link to={`/plans/${planId}/workouts/${w.id}`} className="flex min-w-0 flex-1 items-center gap-3 p-4">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
                    {w.kind === "cardio" ? <Footprints className="size-5" /> : <Dumbbell className="size-5" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 font-semibold">
                      {w.name}
                      {w.kind === "cardio" && <Badge variant="secondary">{t("kinds.cardio")}</Badge>}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {w.exercises.length ? w.exercises.join(", ") : w.focus}
                    </span>
                    {w.minutes > 0 && (
                      <span className="block text-xs font-medium text-muted-foreground">
                        {td("duration", { min: w.minutes })}
                      </span>
                    )}
                  </span>
                  <ChevronRight className="size-5 shrink-0 text-muted-foreground" />
                </Link>
                <div className="flex flex-col border-l">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    disabled={i === 0 || pending}
                    aria-label={tc("moveUp")}
                    onClick={() => start(() => moveWorkout(w.id, "up"))}
                  >
                    <ArrowUp />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    disabled={i === workouts.length - 1 || pending}
                    aria-label={tc("moveDown")}
                    onClick={() => start(() => moveWorkout(w.id, "down"))}
                  >
                    <ArrowDown />
                  </Button>
                </div>
              </div>
            </Card>
          </li>
        ))}
      </ul>
      <Button
        variant="outline"
        className="mt-2 w-full"
        disabled={pending}
        onClick={() =>
          start(async () => {
            const name = t("newWorkoutName", { letter });
            const id = await addWorkout(planId, { de: name, en: name }, "strength");
            navigate(`/plans/${planId}/workouts/${id}`);
          })
        }
      >
        <Plus /> {t("addWorkout")}
      </Button>
    </Section>
  );
}

function PhasesSection({ plan, locale }: { plan: PlanData; locale: Locale }) {
  const t = useTranslations("planEditor");
  const tc = useTranslations("common");
  const [phases, setPhases] = useState<Phase[]>(plan.phases);
  const [pending, save] = useSaver();
  const update = (id: string, patch: Partial<Phase>) =>
    setPhases((ps) => ps.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  // Digits only: anything else would turn into NaN, which the API rejects.
  const num = (v: string) => {
    const digits = v.replace(/\D/g, "");
    return digits === "" ? undefined : Number(digits);
  };

  return (
    <Section title={t("phases")}>
      <p className="-mt-1 mb-2 text-xs text-muted-foreground">{t("phasesHelp")}</p>
      <div className="space-y-2">
        {phases.map((p) => (
          <Card key={p.id} className="py-4">
            <CardContent className="space-y-3 px-4">
              <div className="flex items-center justify-between">
                <span className="font-semibold">
                  {p.toWeek ? t("phase", { from: p.fromWeek, to: p.toWeek }) : t("phaseOpen", { from: p.fromWeek })}
                </span>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={tc("delete")}
                  onClick={() => setPhases((ps) => ps.filter((x) => x.id !== p.id))}
                >
                  <Trash2 />
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="grid gap-1">
                  <Label className="text-xs">{t("fromWeek")}</Label>
                  <Input
                    inputMode="numeric"
                    value={p.fromWeek}
                    onChange={(e) => update(p.id, { fromWeek: num(e.target.value) ?? 1 })}
                  />
                </div>
                <div className="grid gap-1">
                  <Label className="text-xs">{t("toWeek")}</Label>
                  <Input
                    inputMode="numeric"
                    value={p.toWeek ?? ""}
                    placeholder="∞"
                    onChange={(e) => update(p.id, { toWeek: num(e.target.value) ?? null })}
                  />
                </div>
                <div className="grid gap-1">
                  <Label className="text-xs">{t("rir")}</Label>
                  <Input value={p.rir} onChange={(e) => update(p.id, { rir: e.target.value })} />
                </div>
              </div>
              <div className="grid gap-1">
                <Label className="text-xs">{t("setsMode")}</Label>
                <Select value={p.setsMode} onValueChange={(v) => update(p.id, { setsMode: v as Phase["setsMode"] })}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(["plan", "fixed", "bonus"] as const).map((m) => (
                      <SelectItem key={m} value={m}>
                        {t(`setsModes.${m}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {p.setsMode === "fixed" && (
                <div className="grid gap-1">
                  <Label className="text-xs">{t("fixedSets")}</Label>
                  <Input
                    inputMode="numeric"
                    value={p.fixedSets ?? ""}
                    onChange={(e) => update(p.id, { fixedSets: num(e.target.value) })}
                  />
                </div>
              )}
              {p.setsMode === "bonus" && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="grid gap-1">
                    <Label className="text-xs">{t("bonusSets")}</Label>
                    <Input
                      inputMode="numeric"
                      value={p.bonusSets ?? ""}
                      onChange={(e) => update(p.id, { bonusSets: num(e.target.value) })}
                    />
                  </div>
                  <div className="grid gap-1">
                    <Label className="text-xs">{t("bonusFirstN")}</Label>
                    <Input
                      inputMode="numeric"
                      value={p.bonusFirstN ?? ""}
                      onChange={(e) => update(p.id, { bonusFirstN: num(e.target.value) })}
                    />
                  </div>
                </div>
              )}
              <div className="grid gap-1">
                <Label className="text-xs">{t("goal")}</Label>
                <Textarea
                  value={p.goal[locale] ?? ""}
                  onChange={(e) => update(p.id, { goal: { ...p.goal, [locale]: e.target.value } })}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <Button
          variant="outline"
          onClick={() => {
            const last = phases.at(-1);
            const from = last?.toWeek ? last.toWeek + 1 : (last?.fromWeek ?? 0) + 4;
            setPhases((ps) => [
              ...ps,
              { id: crypto.randomUUID(), fromWeek: from, toWeek: null, setsMode: "plan", rir: "2", goal: {} },
            ]);
          }}
        >
          <Plus /> {t("addPhase")}
        </Button>
        <Button disabled={pending} onClick={() => save(() => updatePlan(plan.id, { phases }))}>
          {pending && <Loader2 className="animate-spin" />} {tc("save")}
        </Button>
      </div>
    </Section>
  );
}

function ListSection({ plan, locale, field }: { plan: PlanData; locale: Locale; field: "warmup" | "tips" }) {
  const t = useTranslations("planEditor");
  const tc = useTranslations("common");
  const [text, setText] = useState((plan[field][locale] ?? []).join("\n"));
  const [pending, save] = useSaver();
  return (
    <Section title={t(field)}>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} className="min-h-32 text-base" />
      <div className="mt-2 flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{tc("oneLinePerItem")}</p>
        <Button
          disabled={pending}
          onClick={() =>
            save(() =>
              updatePlan(plan.id, {
                [field]: {
                  ...plan[field],
                  [locale]: text
                    .split("\n")
                    .map((l) => l.trim())
                    .filter(Boolean),
                },
              }),
            )
          }
        >
          {pending && <Loader2 className="animate-spin" />} {tc("save")}
        </Button>
      </div>
    </Section>
  );
}
